import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { sanitizeDeep } from '../utils/sanitize';
import { storage } from '../utils/storage';
import { getBasename } from '../utils/basename';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Función para obtener el organizationId dinámicamente
const getOrganizationId = () => {
  // Primero intentar obtener del localStorage (usuario autenticado)
  const organizationId = storage.get('organizationId');
  if (organizationId) {
    return organizationId;
  }

  // Fallback a la variable de entorno para desarrollo/testing
  const fallbackId = process.env.REACT_APP_RESTAURANT_ID || 'rest-001';
  console.warn(`No se encontró organizationId en localStorage. Usando fallback: ${fallbackId}`);
  return fallbackId;
};

// Función para obtener el restaurantId seleccionado
const getRestaurantId = () => {
  // Intentar obtener del localStorage (restaurante seleccionado)
  const restaurantId = storage.get('selectedRestaurantId');
  if (restaurantId) {
    return restaurantId;
  }

  // Fallback a la variable de entorno para desarrollo/testing
  const fallbackId = process.env.REACT_APP_RESTAURANT_ID || 'rest-001';
  console.warn(
    `No se encontró selectedRestaurantId en localStorage. Usando fallback: ${fallbackId}`
  );
  return fallbackId;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token de autenticación a todas las peticiones
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        // Citame authorizer (COGNITO_USER_POOLS) espera el idToken CRUDO, sin el
        // prefijo `Bearer` — ver whatsapp-scheduler/docs/admin-api.md.
        config.headers.Authorization = token;
      }

      // Citame: la tenancy (businessId) la deriva el authorizer del backend desde
      // el claim Cognito `custom:businessId` del idToken — NO se mandan los headers
      // Resto `X-Organization-Id` / `X-Restaurant-Id`. Esos headers custom NO están
      // en el allowlist CORS del backend Citame (`cors: true` sólo habilita el set
      // estándar) y dispararían un preflight rechazado contra endpoints válidos
      // (NER-1774).
    } catch (error) {
      console.error('[API Interceptor] Error obteniendo token:', error.message);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para sanitizar inputs HTML en requests de escritura
api.interceptors.request.use(
  (config) => {
    const method = (config.method || '').toLowerCase();
    if (
      ['post', 'put', 'patch'].includes(method) &&
      config.data &&
      typeof config.data === 'object'
    ) {
      config.data = sanitizeDeep(config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // No redirigir en endpoints que manejan 401/404 como parte de su flujo normal
      const url = error.config?.url || '';
      const isOrganizationEndpoint = url.includes('/organizations');

      if (!isOrganizationEndpoint) {
        // Si recibimos un 401 en otros endpoints, redirigir a login.
        // Hard redirect (fuera del Router): DEBE incluir el prefijo de la
        // vertical activa (/consultame/admin o /citame/admin) — un `/login`
        // pelado cae en un path sin behavior de CloudFront (404) y pierde el
        // chrome Consultame (login gap NER-4044 → NER-4111).
        console.error('No autorizado, redirigiendo a login...');
        window.location.href = `${getBasename()}/login`;
      }
    }
    return Promise.reject(error);
  }
);

// Restaurant
export const listRestaurants = async () => {
  const response = await api.get('/restaurants');
  return response.data;
};

export const getRestaurant = async (restaurantId) => {
  const response = await api.get(`/restaurants/${restaurantId}`);
  return response.data;
};

export const createRestaurant = async (data) => {
  const response = await api.post('/restaurants', data);
  return response.data;
};

export const updateRestaurant = async (restaurantId, data) => {
  const organizationId = restaurantId || getOrganizationId();
  const response = await api.put(`/restaurants/${organizationId}`, data);
  return response.data;
};

export const deleteRestaurant = async (restaurantId) => {
  const response = await api.delete(`/restaurants/${restaurantId}`);
  return response.data;
};

// Order modes / delivery settings (NER-1237)
// PATCH /restaurants/{restaurantId}/settings — body { orderModes, deliverySettings, restaurantLocation, ownDelivery }
// 403 con header X-Required-Plan si el plan no incluye `delivery`.
export const updateRestaurantSettings = async (restaurantId, settings) => {
  const response = await api.patch(`/restaurants/${restaurantId}/settings`, settings);
  return response.data;
};

// Repartidores propios (own delivery)
export const getRiders = async (restaurantId) => {
  const response = await api.get(`/restaurants/${restaurantId}/riders`);
  return response.data;
};

export const createRider = async (restaurantId, data) => {
  const response = await api.post(`/restaurants/${restaurantId}/riders`, data);
  return response.data;
};

export const updateRider = async (restaurantId, riderId, data) => {
  const response = await api.patch(`/restaurants/${restaurantId}/riders/${riderId}`, data);
  return response.data;
};

export const deleteRider = async (restaurantId, riderId) => {
  const response = await api.delete(`/restaurants/${restaurantId}/riders/${riderId}`);
  return response.data;
};

// Menu
export const getMenu = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/menu/restaurants/${restaurantId}`);
  return response.data;
};

export const createCategory = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/categories`, data);
  return response.data;
};

export const createItem = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/items`, data);
  return response.data;
};

export const updateCategory = async (categoryId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/categories/${categoryId}`,
    data
  );
  return response.data;
};

export const updateItem = async (itemId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/menu/restaurants/${restaurantId}/items/${itemId}`, data);
  return response.data;
};

export const deleteCategory = async (categoryId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/categories/${categoryId}`);
  return response.data;
};

export const deleteItem = async (itemId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/items/${itemId}`);
  return response.data;
};

export const toggleItemAvailability = async (itemId) => {
  const restaurantId = getRestaurantId();
  const response = await api.patch(
    `/menu/restaurants/${restaurantId}/items/${itemId}/availability`
  );
  return response.data;
};

export const getUploadUrl = async (resourceType, resourceId, contentType) => {
  const restaurantId = getRestaurantId();
  // Convert plural to singular for backend
  const type = resourceType === 'categories' ? 'category' : 'item';
  const response = await api.post(`/menu/restaurants/${restaurantId}/upload-url`, {
    type,
    id: resourceId,
    contentType,
  });
  return response.data;
};

// Tables
export const getTables = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/tables/restaurants/${restaurantId}`);
  return response.data;
};

export const createTable = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}`, data);
  return response.data;
};

export const updateTable = async (tableNumber, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/tables/restaurants/${restaurantId}/${tableNumber}`, data);
  return response.data;
};

export const deleteTable = async (tableNumber) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/tables/restaurants/${restaurantId}/${tableNumber}`);
  return response.data;
};

export const generateQR = async (tableNumber) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}/${tableNumber}/qr`);
  return response.data;
};

// Table Groups
export const getGroups = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/tables/restaurants/${restaurantId}/groups`);
  return response.data;
};

export const createGroup = async ({ name, tableNumbers }) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}/groups`, {
    name,
    tableNumbers,
  });
  return response.data;
};

export const updateGroup = async (groupId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/tables/restaurants/${restaurantId}/groups/${groupId}`, data);
  return response.data;
};

export const removeGroup = async (groupId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/tables/restaurants/${restaurantId}/groups/${groupId}`);
  return response.data;
};

export const closeSession = async (sessionId, forceClose = false) => {
  const response = await api.post(`/sessions/${sessionId}/close`, { forceClose });
  return response.data;
};

// Devuelve los pedidos de una sesión con totales (totalSession/totalPaid/totalUnpaid).
// Usado para advertir de pedidos impagos antes de cerrar una mesa.
export const getSessionOrders = async (sessionId) => {
  const response = await api.get(`/orders/sessions/${sessionId}`);
  return response.data;
};

// Orders
export const getOrders = async (options = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams();

  // Soportar parámetros individuales o objeto de opciones
  const { status, date, startDate, endDate, limit } = options;

  if (status) params.append('status', status);
  if (date) params.append('date', date);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (limit) params.append('limit', limit);

  const response = await api.get(`/orders/restaurants/${restaurantId}?${params.toString()}`);
  return response.data;
};

// Alias for invoicing module compatibility
export const getOrdersByRestaurant = (restaurantId) => getOrders();

// Update order (status, discount, etc.)
export const updateOrder = async (orderId, updates) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/orders/${orderId}?restaurantId=${restaurantId}`, updates);
  return response.data;
};

export const updateOrderStatus = async (orderId, status) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/orders/${orderId}/status?restaurantId=${restaurantId}`, {
    status,
  });
  return response.data;
};

// Stats - Estadísticas de ventas
export const getStats = async (options = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams();

  const { period = 'today', startDate, endDate } = options;

  params.append('period', period);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const response = await api.get(`/orders/restaurants/${restaurantId}/stats?${params.toString()}`);
  return response.data;
};

// Organizations
export const createOrganization = async (data) => {
  const response = await api.post('/organizations', data);
  return response.data;
};

export const getMyOrganization = async () => {
  const response = await api.get('/organizations/me');
  return response.data;
};

export const updateOrganization = async (organizationId, data) => {
  const response = await api.put(`/organizations/${organizationId}`, data);
  return response.data;
};

export const inviteUser = async (organizationId, data) => {
  const response = await api.post(`/organizations/${organizationId}/invite`, data);
  return response.data;
};

export const listOrganizationUsers = async (organizationId) => {
  const response = await api.get(`/organizations/${organizationId}/users`);
  return response.data;
};

// Multi-tenancy: Obtener restaurantes del usuario en una organización
export const listMyRestaurants = async (organizationId) => {
  const response = await api.get(`/organizations/${organizationId}/my-restaurants`);
  return response.data;
};

export const editUser = async (organizationId, userId, data) => {
  const response = await api.put(`/organizations/${organizationId}/users/${userId}`, data);
  return response.data;
};

export const generateAvatarUploadUrl = async (organizationId, userId, contentType) => {
  const response = await api.post(
    `/organizations/${organizationId}/users/${userId}/avatar-upload-url`,
    {
      contentType,
    }
  );
  return response.data;
};

export const toggleUserStatus = async (organizationId, userId, currentStatus) => {
  const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
  const response = await api.patch(`/organizations/${organizationId}/users/${userId}/status`, {
    status: newStatus,
  });
  return response.data;
};

// Eliminar usuario de la organización (borrado lógico)
export const deleteUser = async (organizationId, userId) => {
  const response = await api.delete(`/organizations/${organizationId}/users/${userId}`);
  return response.data;
};

// Obtener usuarios con acceso a un restaurante
export const getRestaurantUsers = async (organizationId, restaurantId) => {
  const response = await api.get(
    `/organizations/${organizationId}/restaurants/${restaurantId}/users`
  );
  return response.data;
};

// Asignar usuarios a un restaurante
export const assignUsersToRestaurant = async (organizationId, restaurantId, userIds) => {
  const response = await api.put(
    `/organizations/${organizationId}/restaurants/${restaurantId}/users`,
    { userIds }
  );
  return response.data;
};

// ========================================
// Cash Registers - Control de Caja
// ========================================

// Get all cash registers for a restaurant
export const getCashRegisters = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/cash-registers?restaurantId=${restaurantId}`);
  return response.data;
};

// Create a new cash register
export const createCashRegister = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post('/cash-registers', {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Update cash register
export const updateCashRegister = async (registerId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/cash-registers/${registerId}`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Open a cash shift
export const openCashShift = async (registerId, openingAmount) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/open`, {
    openingAmount,
    restaurantId,
  });
  return response.data;
};

// Close a cash shift
export const closeCashShift = async (
  registerId,
  shiftId,
  closingAmount,
  notes = null,
  cashCount = null
) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/${shiftId}/close`, {
    closingAmount,
    notes,
    restaurantId,
    cashCount,
  });
  return response.data;
};

// Get shifts for a register
export const getCashShifts = async (registerId, filters = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams({ restaurantId });

  if (filters.status) params.append('status', filters.status);
  if (filters.date) params.append('date', filters.date);

  const response = await api.get(`/cash-registers/${registerId}/shifts?${params.toString()}`);
  return response.data;
};

// Get a single shift
export const getCashShift = async (registerId, shiftId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(
    `/cash-registers/${registerId}/shifts/${shiftId}?restaurantId=${restaurantId}`
  );
  return response.data;
};

// Create a cash transaction (IN/OUT)
export const createCashTransaction = async (registerId, shiftId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/${shiftId}/transactions`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Get transactions for a shift
export const getCashTransactions = async (registerId, shiftId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(
    `/cash-registers/${registerId}/shifts/${shiftId}/transactions?restaurantId=${restaurantId}`
  );
  return response.data;
};

// ========================================
// Customers — ver sección "Citame Scheduling" más abajo. En Citame los clientes
// son read-only (se crean solos al escribir por WhatsApp, keyed by phone); no hay
// CRUD ni transacciones/balance como en el admin Resto original.
// ========================================

// ========================================
// Inventory & Expenses
// ========================================

// Inventory
export const getInventoryItems = async () => {
  const response = await api.get('/inventory');
  return response.data;
};

export const createInventoryItem = async (data) => {
  const response = await api.post('/inventory', data);
  return response.data;
};

export const updateInventoryItem = async (itemId, data) => {
  const response = await api.put(`/inventory/${itemId}`, data);
  return response.data;
};

export const deleteInventoryItem = async (itemId) => {
  const response = await api.delete(`/inventory/${itemId}`);
  return response.data;
};

// Inventory Movements
export const getInventoryMovements = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.itemId) urlParams.append('itemId', params.itemId);
  if (params.startDate) urlParams.append('startDate', params.startDate);
  if (params.endDate) urlParams.append('endDate', params.endDate);

  const response = await api.get(`/inventory/movements?${urlParams.toString()}`);
  return response.data;
};

export const createInventoryMovement = async (data) => {
  const response = await api.post('/inventory/movements', data);
  return response.data;
};
// End Inventory Movements

// Expenses
export const getExpenses = async () => {
  const response = await api.get('/expenses');
  return response.data;
};

export const createExpense = async (data) => {
  const response = await api.post('/expenses', data);
  return response.data;
};

// ========================================
// Suppliers
// ========================================

export const getSuppliers = async () => {
  const response = await api.get('/suppliers');
  return response.data;
};

export const createSupplier = async (data) => {
  const response = await api.post('/suppliers', data);
  return response.data;
};

export const updateSupplier = async (supplierId, data) => {
  const response = await api.put(`/suppliers/${supplierId}`, data);
  return response.data;
};

export const deleteSupplier = async (supplierId) => {
  const response = await api.delete(`/suppliers/${supplierId}`);
  return response.data;
};

export const addSupplierTransaction = async (supplierId, data) => {
  const response = await api.post(`/suppliers/${supplierId}/transactions`, data);
  return response.data;
};

export const getSupplierTransactions = async (supplierId) => {
  const response = await api.get(`/suppliers/${supplierId}/transactions`);
  return response.data;
};

// ========================================
// Purchase Orders
// ========================================

export const createPurchaseOrder = async (data) => {
  const response = await api.post('/purchase-orders', data);
  return response.data;
};

export const getPurchaseOrders = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.supplierId) urlParams.append('supplierId', params.supplierId);
  if (params.status) urlParams.append('status', params.status);

  const response = await api.get(`/purchase-orders?${urlParams.toString()}`);
  return response.data;
};

export const updatePurchaseOrderStatus = async (orderId, status) => {
  const response = await api.put(`/purchase-orders/${orderId}/status`, { status });
  return response.data;
};

// ========================================
// Modifier Groups
// ========================================

export const getModifierGroups = async (restaurantId) => {
  if (!restaurantId) return { modifierGroups: [] };
  const response = await api.get(`/menu/restaurants/${restaurantId}/modifier-groups`);
  return response.data;
};

export const createModifierGroup = async (restaurantId, data) => {
  const response = await api.post(`/menu/restaurants/${restaurantId}/modifier-groups`, data);
  return response.data;
};

export const updateModifierGroup = async (restaurantId, groupId, data) => {
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/modifier-groups/${groupId}`,
    data
  );
  return response.data;
};

export const deleteModifierGroup = async (restaurantId, groupId) => {
  const response = await api.delete(`/menu/restaurants/${restaurantId}/modifier-groups/${groupId}`);
  return response.data;
};

// ========================================
// Subscriptions
// ========================================

export const getPlans = async () => {
  const response = await api.get('/plans');
  return response.data;
};

export const getPlan = async (planId) => {
  const response = await api.get(`/plans/${planId}`);
  return response.data;
};

export const getSubscriptions = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.organizationId) urlParams.append('organizationId', params.organizationId);
  if (params.status) urlParams.append('status', params.status);

  const response = await api.get(`/subscriptions?${urlParams.toString()}`);
  return response.data;
};

export const getSubscription = async (subscriptionId) => {
  const response = await api.get(`/subscriptions/${subscriptionId}`);
  return response.data;
};

export const createSubscription = async (data) => {
  const response = await api.post('/subscriptions', data);
  return response.data;
};

export const updateSubscription = async (subscriptionId, data) => {
  const response = await api.put(`/subscriptions/${subscriptionId}`, data);
  return response.data;
};

export const activateMercadoPagoSubscription = async (subscriptionId, payerEmail) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/activate-mp`, { payerEmail });
  return response.data;
};

export const cancelSubscription = async (subscriptionId, immediate = false) => {
  const urlParams = new URLSearchParams();
  if (immediate) urlParams.append('immediate', 'true');

  const response = await api.delete(`/subscriptions/${subscriptionId}?${urlParams.toString()}`);
  return response.data;
};

export const getSubscriptionUsage = async (subscriptionId) => {
  const response = await api.get(`/subscriptions/${subscriptionId}/usage`);
  return response.data;
};

export const getSubscriptionTransactions = async (subscriptionId, params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.limit) urlParams.append('limit', params.limit);
  if (params.lastKey) urlParams.append('lastKey', params.lastKey);

  const queryString = urlParams.toString();
  const url = `/subscriptions/${subscriptionId}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await api.get(url);
  return response.data;
};

export const createPaymentMethod = async (subscriptionId, data) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/payment-method`, data);
  return response.data;
};

export const getAddons = async (planId) => {
  const response = await api.get('/addons', { params: planId ? { planId } : {} });
  return response.data;
};

export const toggleAddon = async (subscriptionId, addOnId, activate) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/addons`, {
    addOnId,
    action: activate ? 'activate' : 'deactivate',
  });
  return response.data;
};

export const getPlanFeatureMatrix = async () => {
  const response = await api.get('/plans/feature-matrix');
  return response.data;
};

// ========================================
// Delivery Integrations
// ========================================

export const getDeliveryIntegrations = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/delivery/integrations/${id}`);
  return response.data;
};

export const getDeliveryIntegrationConfig = async (restaurantId, platform) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/delivery/integrations/${id}/${platform}`);
  return response.data;
};

export const updateDeliveryIntegrationConfig = async (restaurantId, platform, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.put(`/delivery/integrations/${id}/${platform}`, data);
  return response.data;
};

// ========================================
// Invoicing - Facturación Electrónica AFIP
// ========================================

export const getFiscalConfig = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/restaurants/${id}/fiscal-config`);
  return response.data;
};

export const saveFiscalConfig = async (restaurantId, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.put(`/invoicing/restaurants/${id}/fiscal-config`, data);
  return response.data;
};

export const generateAfipCSR = async (restaurantId, data = {}) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.post(`/invoicing/restaurants/${id}/generate-csr`, data);
  return response.data;
};

export const deleteAfipCertificate = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.delete(`/invoicing/restaurants/${id}/certificate`);
  return response.data;
};

export const uploadAfipCertificate = async (restaurantId, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.post(`/invoicing/restaurants/${id}/certificate`, data);
  return response.data;
};

export const getAfipStatus = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/restaurants/${id}/afip-status`);
  return response.data;
};

export const createInvoice = async (data) => {
  const response = await api.post('/invoicing/invoices', data);
  return response.data;
};

export const getInvoice = async (invoiceId, restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/invoices/${invoiceId}?restaurantId=${id}`);
  return response.data;
};

export const listInvoices = async (restaurantId, params = {}) => {
  const id = restaurantId || getRestaurantId();
  const query = new URLSearchParams(params).toString();
  const response = await api.get(`/invoicing/restaurants/${id}/invoices?${query}`);
  return response.data;
};

export const createCreditNote = async (data) => {
  const response = await api.post('/invoicing/credit-notes', data);
  return response.data;
};

export const createDebitNote = async (data) => {
  const response = await api.post('/invoicing/debit-notes', data);
  return response.data;
};

export const getInvoicePdf = async (invoiceId, restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/invoices/${invoiceId}/pdf?restaurantId=${id}`);
  return response.data;
};

// ==================== Custom Domains ====================

export const listCustomDomains = async () => {
  const response = await api.get('/custom-domains');
  return response.data;
};

export const registerCustomDomain = async (hostname, type = 'menu') => {
  const response = await api.post('/custom-domains', { hostname, type });
  return response.data;
};

export const removeCustomDomain = async (hostname) => {
  const response = await api.delete(`/custom-domains/${encodeURIComponent(hostname)}`);
  return response.data;
};

export const verifyCustomDomain = async (hostname) => {
  const response = await api.post(`/custom-domains/${encodeURIComponent(hostname)}/verify`);
  return response.data;
};

// MercadoPago
export const testMercadoPagoToken = async (accessToken) => {
  const response = await api.post('/payments/mercadopago/test', { accessToken });
  return response.data;
};

// Chat / Support Messages
// Citame: la tenancy (businessId) la deriva el authorizer del backend desde el
// claim Cognito `custom:businessId` — NO se manda restaurantId como en Resto
// (ver interceptor + NER-1774). `source: 'citame'` etiqueta el inbound para que
// el operador humano distinga el origen en el inbox unificado (NER-2688).
const CHAT_SOURCE = 'citame';

export const getChatMessages = async () => {
  const response = await api.get('/chat/messages');
  return response.data;
};

export const sendChatMessage = async (message) => {
  const response = await api.post('/chat/messages', { message, source: CHAT_SOURCE });
  return response.data;
};

export const markChatMessagesRead = async () => {
  const response = await api.post('/chat/messages/read', {});
  return response.data;
};

// Promotion Rules
export const getPromotionRules = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/menu/restaurants/${restaurantId}/promotion-rules`);
  return response.data;
};

export const createPromotionRule = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/promotion-rules`, data);
  return response.data;
};

export const updatePromotionRule = async (ruleId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/promotion-rules/${ruleId}`,
    data
  );
  return response.data;
};

export const deletePromotionRule = async (ruleId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/promotion-rules/${ruleId}`);
  return response.data;
};

export const togglePromotionRule = async (ruleId, isActive) => {
  const restaurantId = getRestaurantId();
  const response = await api.patch(`/menu/restaurants/${restaurantId}/promotion-rules/${ruleId}`, {
    isActive,
  });
  return response.data;
};

// Enterprise Leads
export const submitEnterpriseLead = async (data) => {
  const response = await api.post('/enterprise-leads', data);
  return response.data;
};

// ========================================
// Citame Scheduling (NER-1386)
// ========================================
// Tenancy (organizationId) viaja en el header `X-Organization-Id` que agrega el
// interceptor de request. Estos endpoints son el contrato del admin REST API de
// Citame, pendiente de implementar en backend `whatsapp-scheduler` (ver issue
// de dependencia enlazado en NER-1386). El schema DynamoDB (NER-1396) ya sirve
// estos patrones: appointments del dia = Query PK=ORG#{orgId}#{date},
// begins_with(SK, APPT#); profs/services = Query PK=ORG#{orgId}, begins_with(SK, …).

// Turnos de un dia (YYYY-MM-DD). Read-only para la AgendaPage (M1).
export const getAppointments = async (date) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  const qs = params.toString();
  const response = await api.get(`/appointments${qs ? `?${qs}` : ''}`);
  return response.data;
};

// Rango de turnos (NER-1549). El backend hoy sólo expone /appointments por día
// único; hasta que exista una range query real (sub-dep backend pendiente), el
// admin itera día por día en el cliente y aplana. Cap defensivo de días para no
// disparar cientos de requests (vista mes = ~6 semanas → ≤42 días). Cada turno
// se etiqueta con su `date` (si el backend no la trae, usamos el día consultado).
const APPOINTMENTS_RANGE_MAX_DAYS = 45;
export const getAppointmentsRange = async (startDate, endDate) => {
  const days = [];
  let cursor = startDate;
  while (cursor <= endDate && days.length < APPOINTMENTS_RANGE_MAX_DAYS) {
    days.push(cursor);
    const [y, m, d] = cursor.split('-').map(Number);
    const dt = new Date(y, m - 1, d + 1);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    cursor = `${yy}-${mm}-${dd}`;
  }
  const perDay = await Promise.all(
    days.map(async (date) => {
      const res = await getAppointments(date);
      return (res?.appointments || []).map((a) => ({ ...a, date: a.date || date }));
    })
  );
  return {
    appointments: perDay.flat(),
    days,
    truncated: days.length >= APPOINTMENTS_RANGE_MAX_DAYS,
  };
};

// Cancelar / reagendar / confirmar / no-show un turno (NER-1478).
//   body.status: pending|confirmed|cancelled|no_show|completed
//   body.startTime: 'YYYY-MM-DDTHH:mm' (ISO local) para reagendar; body.professionalId opcional.
// Cancelar/reagendar dispara el mensaje WhatsApp al cliente (side-effect del backend).
// El appointmentId es compuesto ("date#time#prof") → encodeURIComponent obligatorio.
export const patchAppointment = async (appointmentId, body) => {
  const response = await api.patch(`/appointments/${encodeURIComponent(appointmentId)}`, body);
  return response.data;
};

// Profesionales del negocio (medicos, peluqueros, mecanicos…). Columnas de la agenda.
export const getProfessionals = async () => {
  const response = await api.get('/professionals');
  return response.data;
};

// Servicios ofrecidos (consulta, corte, alineacion…) con duracion + buffer.
export const getServices = async () => {
  const response = await api.get('/services');
  return response.data;
};

// ── Settings (org) (NER-1478) ────────────────────────────────────────────────
// GET /settings -> { settings: { businessName, timezone, branding:{logoUrl,colorHex},
//   paymentMethods:[], defaultServiceDurationMin, whatsappTemplates:[] } }
// PUT /settings: merge PARCIAL — mandar SOLO las keys editables presentes; las
// omitidas se preservan, branding hace deep-merge. whatsappTemplates es read-only.
export const getSettings = async () => {
  const response = await api.get('/settings');
  return response.data;
};

export const updateSettings = async (patch) => {
  const response = await api.put('/settings', patch);
  return response.data;
};

// Sedes del negocio (consultorio principal, box 1, bahia 2…). Resuelve el nombre
// de la sede a partir del locationId que traen los profesionales.
export const getLocations = async () => {
  const response = await api.get('/locations');
  return response.data;
};

// ── Customers (read + search) (NER-1478) ─────────────────────────────────────
// GET /customers?search=&limit= -> { customers: [{ customerId, name, phone,
//   appointmentsCount?, lastAppointmentAt? }] }. customerId === phone (E.164 sin +).
// search matchea por nombre (sin acentos/case-insensitive) o por teléfono (dígitos).
// Sin search -> lista completa acotada por limit (default 50, máx 200). Read-only:
// los clientes se crean solos al escribir por WhatsApp.
export const getCustomers = async (search = '', limit) => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  const response = await api.get(`/customers${qs ? `?${qs}` : ''}`);
  return response.data;
};

// GET /customers/{id} -> { customer: {...}, appointments: [{ appointmentId,
//   startTime, status, serviceName }] } (historial vía GSI2, más reciente primero).
export const getCustomer = async (customerId) => {
  const response = await api.get(`/customers/${encodeURIComponent(customerId)}`);
  return response.data;
};

// ── WhatsApp message log (read-only) (NER-1478) ──────────────────────────────
// GET /conversations?limit= -> { conversations: [{ conversationId, customerId,
//   customerName, customerPhone, lastMessageAt, lastMessagePreview }] } (desc).
// conversationId === phone. Caveat: la persistencia de inbound todavía no está
// cableada (NER-1382/1384) — hoy sólo persisten outbound, así que puede venir vacío.
export const getConversations = async (limit) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  const response = await api.get(`/conversations${qs ? `?${qs}` : ''}`);
  return response.data;
};

// GET /conversations/{id}/messages -> { messages: [{ messageId, direction:
//   "in"|"out", body, templateName?, status?, timestamp }] } (timestamp asc).
export const getConversationMessages = async (conversationId) => {
  const response = await api.get(`/conversations/${encodeURIComponent(conversationId)}/messages`);
  return response.data;
};

// ── Professionals CRUD (NER-1475) ────────────────────────────────────────────
// POST  /professionals          body { name, locationId? }     -> 201 { professional }
// PATCH /professionals/{id}      body { name?, locationId? }    -> 200 { professional }
// DELETE /professionals/{id}     -> 200 { deleted } | 409 has_appointments | 404
export const createProfessional = async (data) => {
  const response = await api.post('/professionals', data);
  return response.data;
};

export const updateProfessional = async (professionalId, data) => {
  const response = await api.patch(`/professionals/${professionalId}`, data);
  return response.data;
};

export const deleteProfessional = async (professionalId) => {
  const response = await api.delete(`/professionals/${professionalId}`);
  return response.data;
};

// ── Availability rules — horario semanal por profesional (NER-1475) ──────────
// GET /professionals/{id}/availability -> { rules: [{ ruleId, dayOfWeek, startTime, endTime }] }
// PUT /professionals/{id}/availability  body { rules: [{ dayOfWeek, startTime, endTime }] }
//   reemplaza TODO el set. dayOfWeek 0-6 (Dom=0, convención JS getDay()); HH:mm 24h.
export const getAvailability = async (professionalId) => {
  const response = await api.get(`/professionals/${professionalId}/availability`);
  return response.data;
};

export const updateAvailability = async (professionalId, rules) => {
  const response = await api.put(`/professionals/${professionalId}/availability`, { rules });
  return response.data;
};

// ── Services CRUD (NER-1475) ─────────────────────────────────────────────────
// POST  /services        body { name, durationMin, priceArs }  -> 201 { service }
// PATCH /services/{id}    body { name?, durationMin?, priceArs? } -> 200 { service }
// DELETE /services/{id}   -> 200 { deleted } | 404
export const createService = async (data) => {
  const response = await api.post('/services', data);
  return response.data;
};

export const updateService = async (serviceId, data) => {
  const response = await api.patch(`/services/${serviceId}`, data);
  return response.data;
};

export const deleteService = async (serviceId) => {
  const response = await api.delete(`/services/${serviceId}`);
  return response.data;
};

// ── Coverage matrix — coberturas/convenios cross-industry (NER-1778) ──────────
// Coberturas por business: obra social (medical), aseguradora (mecánica),
// membresía (estética), convenio corporativo (eventos). El cliente "Particular"
// es una coverage especial (isParticular) que actúa de fallback. `coverageLabel`
// del master reemplaza la palabra "Cobertura" en toda la UI (cross-industry).
// RBAC: sólo OWNER/ADMIN editan master y per-profesional (PUT → 403). Token sin
// custom:role se permite (mismo criterio que NER-1777). Contrato:
// docs/admin-api.md sección "Coverage matrix (NER-1778)".

// GET /coverages -> { master: { coverages[], coverageLabel, templateKey, updatedAt }, templates }
export const getCoverages = async () => {
  const response = await api.get('/coverages');
  return response.data;
};

// PUT /coverages — body { coverages[] } o { templateKey } (+ coverageLabel?).
// coverages[]: [{ key, name, isParticular?, policy, adjustmentType, adjustmentValue }].
//   key ^[a-z][a-z0-9_]*$ único; policy ∈ cover|partial|reject; adjustmentType ∈
//   fixed|percent; a lo sumo una isParticular. PUT con templateKey (sin coverages)
//   aplica un template default y setea su coverageLabel.
export const updateCoverages = async (patch) => {
  const response = await api.put('/coverages', patch);
  return response.data;
};

// GET /professionals/{id}/coverages -> { professionalId, acceptedCoverages[] }
//   acceptedCoverages: [{ coverageKey, priceOverride? }] (subset del master).
export const getProfessionalCoverages = async (professionalId) => {
  const response = await api.get(`/professionals/${professionalId}/coverages`);
  return response.data;
};

// PUT /professionals/{id}/coverages — body { acceptedCoverages[] } → 200/400/403/404.
//   priceOverride es un precio final absoluto (>= 0) que pisa el ajuste de la coverage.
export const updateProfessionalCoverages = async (professionalId, acceptedCoverages) => {
  const response = await api.put(`/professionals/${professionalId}/coverages`, {
    acceptedCoverages,
  });
  return response.data;
};

// GET /coverage-matrix -> { matrix: { coverageLabel, coverages[], professionals[] } }
//   professionals[]: [{ professionalId, name, coverages: [{ coverageKey, accepted,
//   priceOverride: null|num, inheritsDefault }] }] — cells es un array paralelo a
//   master.coverages (forma exacta del backend NER-1778). Mapeo viz:
//   accepted+priceOverride → verde (precio propio) / accepted+inheritsDefault →
//   gris (hereda default) / !accepted → rojo (no acepta).
export const getCoverageMatrix = async () => {
  const response = await api.get('/coverage-matrix');
  return response.data;
};

// ── Metrics API (cross-org, NER-2783 / NER-2785) ────────────────────────────
// Dashboard de métricas por vertical (padel vs medical baseline) que alimenta la
// decisión Path B (NER-2781). Estos endpoints NO son per-tenant: agregan datos de
// todos los negocios de un vertical y sólo los sirve el backend a cuentas Nerdo
// admin (allowlist email/grupo Cognito). Un tenant normal recibe 403.
// `businessType` ∈ 'padel' | 'medical' | 'all' (default 'all' → devuelve cada
// vertical + el combinado para la comparativa side-by-side).
// Contrato: whatsapp-scheduler/docs/admin-api.md (Metrics API).

// GET /metrics/padel — adopción: reservas/org/semana, funnel + tasas, time-to-first.
export const getAdoptionMetrics = async (businessType = 'all', weeks) => {
  const params = { businessType };
  if (weeks) params.weeks = weeks;
  const response = await api.get('/metrics/padel', { params });
  return response.data;
};

// GET /metrics/retention — cohorts de clientes D7/14/30/60/90 (basis client-repeat).
export const getRetentionMetrics = async (businessType = 'all') => {
  const response = await api.get('/metrics/retention', { params: { businessType } });
  return response.data;
};

// GET /metrics/arpu — revenue proxy / ARPU mensual (últimos `months` meses + overall).
export const getArpuMetrics = async (businessType = 'all', months) => {
  const params = { businessType };
  if (months) params.months = months;
  const response = await api.get('/metrics/arpu', { params });
  return response.data;
};

// ── Org Metrics API (per-tenant, NER-3144 / NER-3145) ───────────────────────
// Dashboard de métricas del PROPIO negocio (dueño-facing), scoped al orgId del
// JWT (claim custom:businessId) — NO cross-org. Roles OWNER/ADMIN. Contrato:
// whatsapp-scheduler/.agents/citame/product/org-metrics-spec.md (NER-3143).
// Todos aceptan `range` ∈ '7d'|'30d'|'90d'|'365d' (o 'custom' con from/to
// YYYY-MM-DD) y `professionalId` opcional para filtrar.
const buildOrgMetricsParams = ({ range, from, to, professionalId } = {}) => {
  const params = {};
  if (range) params.range = range;
  if (from) params.from = from;
  if (to) params.to = to;
  if (professionalId) params.professionalId = professionalId;
  return params;
};

// GET /orgs/metrics/summary — 5 KPIs (cards) con Δ% vs período anterior.
export const getOrgMetricsSummary = async (opts = {}) => {
  const response = await api.get('/orgs/metrics/summary', {
    params: buildOrgMetricsParams(opts),
  });
  return response.data;
};

// GET /orgs/metrics/appointments-by-day — serie diaria stacked (completed/cancelled/no_show).
export const getOrgAppointmentsByDay = async (opts = {}) => {
  const response = await api.get('/orgs/metrics/appointments-by-day', {
    params: buildOrgMetricsParams(opts),
  });
  return response.data;
};

// GET /orgs/metrics/revenue-by-professional — top profesionales por revenue (FE muestra top 5).
export const getOrgRevenueByProfessional = async (opts = {}) => {
  const response = await api.get('/orgs/metrics/revenue-by-professional', {
    params: buildOrgMetricsParams(opts),
  });
  return response.data;
};

// GET /orgs/metrics/coverage-distribution — mix de coberturas (donut) con pct.
export const getOrgCoverageDistribution = async (opts = {}) => {
  const response = await api.get('/orgs/metrics/coverage-distribution', {
    params: buildOrgMetricsParams(opts),
  });
  return response.data;
};

// ── Setup Wizard (NER-3256 / backend NER-3255) ──────────────────────────────
// POST /admin/setup-org — provisioning atómico de un org nuevo (org + recursos +
// coberturas + horarios + Cognito invite). SuperAdmin only (el backend gatea por
// allowlist SETUP_ADMIN_EMAILS). El wizard arma el payload según la spec
// setup-wizard-spec.md §"Request body". Devuelve { orgId, ownerEmail, ... } o,
// ante fallo, un 500 con { error, step, orgId } que el Paso 5 muestra amigable.
export const setupOrg = async (payload) => {
  const response = await api.post('/admin/setup-org', payload);
  return response.data;
};

// ── Setup Wizard médico — Consultame (NER-3832 / backend dependency) ─────────
// Reutiliza el admin Citame con `vertical=medical` (Opción A, NER-3656).
// POST /admin/consultame/setup-medical — provisioning del consultorio médico
// (profesional + consultorio + coberturas + compliance/DPA + Cognito). Devuelve
// { orgId, ownerEmail, ... }; ante fallo un 409 (usuario existe) / 400
// (validación) / 500 con { error, step } que el Paso 8 muestra amigable.
export const setupMedical = async (payload) => {
  const response = await api.post('/admin/consultame/setup-medical', payload);
  return response.data;
};

// GET /admin/consultame/coverage-master — catálogo autoritativo de obras
// sociales (IOMA/OSDE/PAMI/…). Devuelve un array o { coverages: [...] }. Si el
// endpoint no está disponible el wizard cae al fallback local (medicalPresets).
export const getCoverageMaster = async () => {
  const response = await api.get('/admin/consultame/coverage-master');
  return response.data;
};

export default api;
